import { AxiosResponse } from "axios";
import { oc } from "ts-optchain";
import { Nullable } from "@/types";

import { DealernetApi } from "./DealernetApi";
import { TmsService } from "./TmsService";
import { IComponentGroupsRequestDto } from "@/types/ComponentGroupsRequestDto";

const CONTROLLER_PATH = "OemManuals";

export class OemManualsService {

  // Get OemManuals Truck Types
  public static async GetTruckTypes(userId: number, brandId: number, productGroupId?: Nullable<number>): Promise<IOemManualsProductGroupsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/TruckTypes`,
      data: { userId, brandId, productGroupId },
    });

    return oc(response as AxiosResponse<IOemManualsProductGroupsResponseDto>).data()!;
  }

  // Get OemManuals Models
  public static async GetModels(userId: number, brandId: number, mcfCostCenterId: number, productGroupId?: Nullable<number>): Promise<IOemManualsProductsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Models`,
      data: { userId, brandId, mcfCostCenterId, productGroupId },
    });
    return oc(response as AxiosResponse<IOemManualsProductsResponseDto>).data()!;
  }

  // Get OemManuals Component Groups
  public static async GetComponentGroups(userId: number, requestDto: IComponentGroupsRequestDto, accountId?: number): Promise<IOemManualsComponentGroupsResponseDto> {
    const data: Record<string, any> = { userId, ...requestDto, accountId: accountId ?? 0 };
    if (requestDto.SerialNumber) {
      data.serialNumber = requestDto.SerialNumber;
    }
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/ComponentGroups`,
      data,
    });
    return oc(response as AxiosResponse<IOemManualsComponentGroupsResponseDto>).data()!;
  }

  // Get OemManuals Components
    public static async GetComponents(publicationId: number, dataSource: string, bomId: number): Promise<any> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Components`,
      data: { publicationId, dataSource, bomId },
    });

    return oc(response as AxiosResponse<any>).data()!;
  }


  // Get OemManuals Parts (returns hierarchical tree; leaf nodes are parts)
  public static async GetParts(userId: number, brandId: number, componentNumber: string): Promise<IOemManualsPartTreeNode[]> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Parts`,
      data: { userId, brandId, componentNumber },
    });

    return oc(response as AxiosResponse<IOemManualsPartTreeNode[]>).data()!;
  }

  /** Recursively extracts leaf parts (nodes with PartId & PartNumber) from a parts tree. */
  public static ExtractLeafParts(nodes: IOemManualsPartTreeNode[]): IOemManualsPartTreeNode[] {
    const leaves: IOemManualsPartTreeNode[] = [];
    for (const node of nodes) {
      if (node.PartId != null && node.PartNumber) {
        leaves.push(node);
      }
      if (node.Components?.length) {
        leaves.push(...OemManualsService.ExtractLeafParts(node.Components));
      }
    }
    return leaves;
  }

  // Get OemManuals Diagram
  public static async GetDiagram(userId: number, brandId: number, diagramFormat?: Nullable<OemManualsDiagramFormat>, extArtId?: Nullable<number>, apArtId?: Nullable<number>): Promise<IOemManualsComponentDiagramResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Diagram`,
      data: { userId, brandId, diagramFormat, extArtId, apArtId },
    });

    return oc(response as AxiosResponse<IOemManualsComponentDiagramResponseDto>).data()!;
  }

  /** Decodes a base64-encoded SVG payload to its UTF-8 markup string; passes raw markup through untouched. */
  public static DecodeSvgPayload(data: string): string {
    if (OemManualsService.IsSvgMarkup(data)) {
      return OemManualsService.ApplyCalloutStyles(data);
    }
    const binary = atob(data);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return OemManualsService.ApplyCalloutStyles(new TextDecoder("utf-8").decode(bytes));
  }

  public static IsSvgMarkup(markup: string): boolean {
    return /^\s*(<\?xml|<svg)/i.test(markup);
  }

  /**
   * True for the part that represents a whole sub-assembly. Its ItemNumber is a hotspot on the
   * main diagram, while the other parts sharing its SubAssemblyArtId are on the sub-assembly diagram.
   */
  public static IsAssemblyHeader(part: any): boolean {
    return part?.SubAssemblyArtId != null && Number(part.AssemblyHeaderNode) === 1;
  }

  /** Identifies a sub-assembly's diagram among the diagrams the viewer can switch between. */
  public static SubAssemblyDiagramKey(artId: number): string {
    return `sub-${artId}`;
  }

  /** Injects/replaces the SVG's <style> block so callout rect/text numbers are visible. */
  private static ApplyCalloutStyles(svg: string): string {
    const calloutStyle = `<style type="text/css"><![CDATA[
  rect {
            fill: white;
            stroke: #2F79A1;
            stroke-width: 3;
            cursor: pointer;
            rx:10;
            ry:10;
        }
  text {
            font-size: 96px;
            fill: #2F79A1;
            cursor: pointer;
       }
  ]]></style>`;

    const stylePattern = /<style[^>]*>[\s\S]*?<\/style>/i;
    if (stylePattern.test(svg)) {
      return svg.replace(stylePattern, calloutStyle);
    }
    return svg.replace(/<svg[^>]*>/i, (openTag) => `${openTag}${calloutStyle}`);
  }
}

// Nullable type helper
type Nullable<T> = T | null;

// ============================================================================
// Enums
// ============================================================================

export enum OemManualsDiagramFormat {
  Png = 'png',
  Svg = 'svg'
}

export enum OemManualsErrorType {
  User = 'User',
  Temp = 'Temp',
  System = 'System'
}

// ============================================================================
// Common/Shared Interfaces
// ============================================================================

export interface IOemManualsHeaderDto {
  UUID: string;
  accountNumber: string;
  locationId: string;
  language: string;
}

export interface IOemManualsErrorDto {
  Type: OemManualsErrorType;
  Message: string;
}

export interface IOemManualsBaseRequestDto {
  userId: number;
}

export interface IOemManualsBaseResponseDto {
  statusCode: number;
  responseHeader: IOemManualsHeaderDto;
  error?: Nullable<IOemManualsErrorDto>;
}

// ============================================================================
// Request Interfaces
// ============================================================================

export interface IOemManualsProductGroupsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface IOemManualsProductsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface IOemManualsComponentGroupsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productId: string;
}

export interface IOemManualsComponentsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentGroupName: string;
}

export interface IOemManualsComponentPartsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentNumber: string;
}

export interface IOemManualsComponentDiagramRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentNumber: string;
  diagramFormat?: Nullable<OemManualsDiagramFormat>;
}

// ============================================================================
// Response DTOs (List Items)
// ============================================================================

export interface IOemManualsProductGroupDto {
  TruckClass: string;
  TruckClassId: number;
}

export interface IOemManualsProductDto {
  ModelIdAndSerialNumberRangeId: string;
  ModelName: string;
}

export interface IOemManualsComponentGroupDto {
  PublicationIdAndDataSource: string;
  PublicationModuleTitle: string;
  ProductId?: number;
  ModelId?: number;
  BrandId?: number;
  ModelName?: string;
  SerialNumberRange?: string;
  PublicationModuleId?: number;
  BomId?: number;
  DataSource?: string;
}

export interface IOemManualsComponentDto {
  PublicationIdAndDataSource: string;
  PublicationModuleTitle: string;
  HasChildComponents: boolean;
  ChildComponents: IOemManualsComponentDto[];
}

export interface IOemManualsComponentPartDto {
  OemPartCode: string;
  PartDescription: string;
  Quantity: string;
  ReferenceNumber: string;
  Notes: string;
}

/** Recursive tree node returned by the GetParts API. Leaf nodes have MasterPartId & PartNumber. */
export interface IOemManualsPartTreeNode {
  SortOrder?: number;
  FullHierarchyPath: string;
  PartDescription: string;
  Components: IOemManualsPartTreeNode[];
  /** Present only on leaf (part) nodes. */
  PartId?: number;
  /** Present only on leaf (part) nodes. */
  PartNumber?: string;
}

// ============================================================================
// Response Interfaces (Root)
// ============================================================================

export interface IOemManualsProductGroupsResponseDto extends IOemManualsBaseResponseDto {
  ProductGroups: IOemManualsProductGroupDto[];
}

export interface IOemManualsProductsResponseDto extends IOemManualsBaseResponseDto {
  Products: IOemManualsProductDto[];
}

export interface IOemManualsComponentGroupsResponseDto extends IOemManualsBaseResponseDto {
  ComponentGroups: IOemManualsComponentGroupDto[];
}

export interface IOemManualsComponentsResponseDto extends IOemManualsBaseResponseDto {
  Components: IOemManualsComponentDto[];
}

export interface IOemManualsComponentPartsResponseDto extends IOemManualsBaseResponseDto {
  ComponentParts: IOemManualsComponentPartDto[];
}

export interface IOemManualsComponentDiagramResponseDto extends IOemManualsBaseResponseDto {
  DiagramData: string;
  DiagramFormat: OemManualsDiagramFormat;
}

/** A single diagram (main component or sub-assembly) the diagram viewer can switch between. */
export interface IOemManualsDiagramEntry {
  key: string;
  title: string;
  /** Empty once loaded means the diagram couldn't be fetched or wasn't SVG. */
  svgData: string;
  loading: boolean;
}

